import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Alert
} from '@mui/material';
import {
  CheckCircle as AttendedIcon,
  RequestQuote as RequestFeeIcon,
  AttachMoney as FeePaidIcon,
  EventBusy as ExcusedIcon
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import { gridTheme, defaultColDef, centeredCellStyle, rightAlignedCellStyle } from '../services/aggrid';
import axios from 'axios';
import { useNavigate, useOutletContext } from 'react-router-dom';
import NoAccess from '../components/NoAccess';
import { Panel, Chip, Btn, Dot, PageHead, I } from '../components/hud';

axios.defaults.withCredentials = true;

const API_BASE_URL = (import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000')) + '/api';

// Arc palette for the SVG ring chart
const ARC_COLORS = [
  'var(--accent)',
  '#5ac8f5',
  '#7ed8ff',
  'var(--magenta)',
  'var(--amber)',
  '#5bd99a'
];

// Compact ISK formatter for big numbers
function fmtISKShort(n) {
  if (n == null) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(2) + ' T';
  if (abs >= 1e9)  return (n / 1e9).toFixed(2) + ' B';
  if (abs >= 1e6)  return (n / 1e6).toFixed(2) + ' M';
  if (abs >= 1e3)  return (n / 1e3).toFixed(2) + ' K';
  return n.toFixed(2);
}

function fmtISK(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount) + ' ISK';
}

function fmtDate(date) {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString();
}

// SVG segmented ring chart — one arc per division
function RingChart({ wallets, total }) {
  const cx = 90, cy = 90, r = 72, stroke = 18;
  const circ = 2 * Math.PI * r;

  let offset = 0;
  const arcs = wallets.map((w, i) => {
    const pct = total > 0 ? w.balance / total : 0;
    const dash = pct * circ;
    const arc = { pct, dash, offset, color: ARC_COLORS[i % ARC_COLORS.length] };
    offset += dash + (total > 0 ? circ * 0.004 : 0); // tiny gap
    return arc;
  });

  return (
    <svg viewBox="0 0 180 180" style={{ width: 180, height: 180, flexShrink: 0 }}>
      {/* track */}
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={stroke} />
      {arcs.map((a, i) => (
        <circle
          key={i}
          cx={cx} cy={cy} r={r}
          fill="none"
          stroke={a.color}
          strokeWidth={stroke}
          strokeDasharray={`${a.dash} ${circ - a.dash}`}
          strokeDashoffset={-a.offset}
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ filter: `drop-shadow(0 0 4px ${a.color})` }}
        />
      ))}
      <text x={cx} y={cy - 8} textAnchor="middle" fill="var(--text-1)" fontSize={13} fontFamily="var(--font-mono)" fontWeight={700}>
        {fmtISKShort(total)}
      </text>
      <text x={cx} y={cy + 10} textAnchor="middle" fill="var(--text-3)" fontSize={9} fontFamily="var(--font-mono)">
        ISK TOTAL
      </text>
    </svg>
  );
}

function Wallet() {
  const navigate = useNavigate();
  const { user } = useOutletContext();
  const [wallets, setWallets] = useState([]);

  // Permission check must come before any other hooks (hooks cannot be conditional)
  const hasAccess = user?.permissions?.canViewWallet;

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Filters
  const [selectedYear, setSelectedYear] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');

  // Pending fees modal state
  const [pendingFeesModalOpen, setPendingFeesModalOpen] = useState(false);
  const [pendingFees, setPendingFees] = useState([]);

  // Fetch wallets from MongoDB
  const fetchWallets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/wallet`);
      setWallets(response.data);
    } catch (err) {
      console.error('Error fetching wallets:', err);
      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch wallets');
    } finally {
      setLoading(false);
    }
  };

  // Sync wallets from ESI
  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSyncMessage(null);

      const response = await axios.post(`${API_BASE_URL}/wallet/sync`);
      setSyncMessage({ type: 'success', message: response.data.message });
      await fetchWallets();

      try {
        const feesResponse = await axios.get(`${API_BASE_URL}/wallet/pending-fees`);
        if (feesResponse.data && feesResponse.data.length > 0) {
          setPendingFees(feesResponse.data);
          setPendingFeesModalOpen(true);
        }
      } catch (feeErr) {
        console.error('Error fetching pending fees:', feeErr);
      }
    } catch (err) {
      console.error('Error syncing wallets:', err);
      if (err.response?.data?.requiresReauth || err.response?.status === 401) {
        navigate('/?error=session_expired');
        return;
      }
      const errorMessage = err.response?.data?.error || 'Failed to sync wallets';
      setError(errorMessage);
      setSyncMessage({ type: 'error', message: errorMessage });
    } finally {
      setSyncing(false);
    }
  };

  // Update attendance status for a pending fee
  const handleUpdateAttendanceStatus = async (eventId, characterId, newStatus) => {
    try {
      const feeItem = pendingFees.find(f => f.eventId === eventId && f.characterId === characterId);
      if (!feeItem) return;

      await axios.put(`${API_BASE_URL}/events/${eventId}/attendance`, {
        characterId,
        characterName: feeItem.characterName,
        status: newStatus
      });

      if (newStatus !== 'request_fee') {
        setPendingFees(prev => prev.filter(f => !(f.eventId === eventId && f.characterId === characterId)));
      } else {
        setPendingFees(prev => prev.map(f =>
          f.eventId === eventId && f.characterId === characterId
            ? { ...f, status: newStatus }
            : f
        ));
      }
    } catch (err) {
      console.error('Error updating attendance status:', err);
      setError(err.response?.data?.error || 'Failed to update attendance');
    }
  };

  // Group pending fees by event
  const pendingFeesByEvent = useMemo(() => {
    const grouped = {};
    pendingFees.forEach(fee => {
      const key = fee.eventId;
      if (!grouped[key]) {
        grouped[key] = {
          eventId: fee.eventId,
          eventName: fee.eventName,
          eventDate: fee.eventDate,
          feeType: fee.feeType,
          baseFee: fee.baseFee,
          attendees: []
        };
      }
      grouped[key].attendees.push(fee);
    });
    return Object.values(grouped).sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));
  }, [pendingFees]);

  useEffect(() => {
    if (hasAccess) fetchWallets();
  }, []);

  // Calculate total balance across all wallets
  const totalBalance = useMemo(() => wallets.reduce((sum, w) => sum + w.balance, 0), [wallets]);

  // Get current wallet based on tab
  const currentWallet = wallets[currentTab];

  // Last sync date (most recent across all wallets)
  const lastSync = useMemo(() => {
    if (!wallets.length) return null;
    const dates = wallets.map(w => w.lastSyncedAt).filter(Boolean).map(d => new Date(d));
    if (!dates.length) return null;
    return new Date(Math.max(...dates));
  }, [wallets]);

  // Available years from current wallet's transactions
  const availableYears = useMemo(() => {
    if (!currentWallet?.transactions) return [];
    const years = new Set();
    currentWallet.transactions.forEach(tx => years.add(new Date(tx.date).getFullYear()));
    return Array.from(years).sort((a, b) => b - a);
  }, [currentWallet]);

  // Filter transactions
  const filteredTransactions = useMemo(() => {
    if (!currentWallet?.transactions) return [];
    let filtered = currentWallet.transactions;

    if (selectedYear !== 'all') {
      filtered = filtered.filter(tx => new Date(tx.date).getFullYear() === parseInt(selectedYear));
    }
    if (typeFilter !== 'all') {
      if (typeFilter === 'market')  filtered = filtered.filter(tx => tx.refType === 'market');
      else if (typeFilter === 'journal') filtered = filtered.filter(tx => tx.refType === 'journal');
      else if (typeFilter === 'buy')  filtered = filtered.filter(tx => tx.refType === 'market' && tx.isBuy);
      else if (typeFilter === 'sell') filtered = filtered.filter(tx => tx.refType === 'market' && !tx.isBuy);
    }
    if (searchFilter.trim()) {
      const q = searchFilter.toLowerCase();
      filtered = filtered.filter(tx =>
        tx.typeName?.toLowerCase().includes(q) ||
        tx.clientName?.toLowerCase().includes(q) ||
        tx.firstPartyName?.toLowerCase().includes(q) ||
        tx.secondPartyName?.toLowerCase().includes(q) ||
        tx.description?.toLowerCase().includes(q)
      );
    }
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [currentWallet, selectedYear, typeFilter, searchFilter]);

  // AG Grid column definitions
  const txColumnDefs = useMemo(() => [
    {
      field: 'date',
      headerName: 'Date',
      width: 170,
      valueGetter: (p) => p.data.date ? new Date(p.data.date) : null,
      filter: 'agDateColumnFilter',
      sort: 'desc',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-mute" style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span>{new Date(params.data.date).toLocaleDateString()}</span>
          <span style={{ fontSize: 10, opacity: 0.6 }}>{new Date(params.data.date).toLocaleTimeString()}</span>
        </span>
      )
    },
    {
      headerName: 'Type',
      width: 130,
      valueGetter: (p) => p.data.refType === 'market' ? (p.data.isBuy ? 'Buy' : 'Sell') : 'Journal',
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => {
        const tx = params.data;
        if (tx.refType === 'market') {
          return (
            <Chip tone={tx.isBuy ? 'danger' : 'ok'}>
              {tx.isBuy ? 'BUY' : 'SELL'}
            </Chip>
          );
        }
        return <Chip tone="amber">JOURNAL</Chip>;
      }
    },
    {
      headerName: 'Description',
      flex: 2,
      minWidth: 240,
      valueGetter: (p) => p.data.refType === 'market' ? (p.data.typeName || 'Unknown') : (p.data.description || ''),
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle
    },
    {
      headerName: 'Party',
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => p.data.refType === 'market'
        ? (p.data.clientName || 'Unknown')
        : (p.data.secondPartyName || p.data.firstPartyName || 'Unknown'),
      filter: 'agTextColumnFilter',
      cellStyle: centeredCellStyle,
      cellRenderer: (params) => (
        <span className="cell-mute">{params.value}</span>
      )
    },
    {
      headerName: 'Details',
      width: 180,
      sortable: false,
      filter: false,
      cellStyle: rightAlignedCellStyle,
      cellRenderer: (params) => {
        const tx = params.data;
        if (tx.refType === 'market') {
          return (
            <span style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
              <span>Qty: {tx.quantity ? tx.quantity.toLocaleString() : '—'}</span>
              <span className="cell-mute" style={{ fontSize: 10 }}>
                @ {tx.unitPrice ? fmtISK(tx.unitPrice) : '—'}
              </span>
            </span>
          );
        }
        return <span className="cell-mute">{tx.reason || '—'}</span>;
      }
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 200,
      filter: 'agNumberColumnFilter',
      cellStyle: rightAlignedCellStyle,
      cellRenderer: (params) => (
        <span className={params.value >= 0 ? 'cell-amt-pos' : 'cell-amt-neg'} style={{ fontWeight: 600 }}>
          {params.value >= 0 ? '+' : ''}{fmtISK(params.value)}
        </span>
      )
    }
  ], []);

  // No-access gate
  if (!hasAccess) {
    return <NoAccess feature="Corporation Wallet" requiredRole="Director, CEO, Accountant, or Junior Accountant" />;
  }

  return (
    <>
      <PageHead
        crumb="OPS // TREASURY"
        title="Corporation Wallets"
        actions={
          <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync from ESI'}
          </Btn>
        }
      />

      {/* Status messages */}
      {syncMessage && (
        <Alert
          severity={syncMessage.type}
          onClose={() => setSyncMessage(null)}
          sx={{ mb: 0 }}
        >
          {syncMessage.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 0 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Panel title="Loading...">
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
            Fetching wallet data…
          </div>
        </Panel>
      ) : wallets.length === 0 ? (
        <Panel title="No Wallet Data">
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
            <div style={{ marginBottom: '1rem' }}>Click "Sync from ESI" to fetch your corporation's wallet information.</div>
            <Btn icon={I.Sync} tone="primary" onClick={handleSync} disabled={syncing}>
              Sync Wallets
            </Btn>
          </div>
        </Panel>
      ) : (
        <>
          {/* Distribution + Treasury row */}
          <div className="grid-2">
            <Panel
              title="Distribution"
              sub={`${wallets.length} DIVISION${wallets.length !== 1 ? 'S' : ''}`}
            >
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <RingChart wallets={wallets} total={totalBalance} />
                <div className="div-list" style={{ flex: 1, minWidth: 160 }}>
                  {wallets.map((w, i) => {
                    const pct = totalBalance > 0 ? (w.balance / totalBalance) * 100 : 0;
                    return (
                      <div key={w.division} className="div-row">
                        <span
                          className="div-swatch"
                          style={{ background: ARC_COLORS[i % ARC_COLORS.length] }}
                        />
                        <span className="nm">Division {w.division}</span>
                        <span className="pct">{pct.toFixed(1)}%</span>
                        <span className="amt">{fmtISKShort(w.balance)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </Panel>

            <Panel
              title="Treasury Snapshot"
              sub={lastSync ? `LAST SYNC ${new Date(lastSync).toLocaleString()}` : 'NOT SYNCED'}
              action={<Chip tone="ok"><Dot />NOMINAL</Chip>}
            >
              <div className="label">TOTAL CORP BALANCE</div>
              <div className="isk-big">
                {fmtISKShort(totalBalance)}<span className="unit">ISK</span>
              </div>
              <div style={{ marginTop: '1rem', color: 'var(--text-3)', fontSize: 11 }}>
                Across {wallets.length} wallet division{wallets.length !== 1 ? 's' : ''}
              </div>
            </Panel>
          </div>

          {/* Transaction Ledger */}
          <Panel
            title="Transaction Ledger"
            sub={currentWallet ? `DIV ${currentWallet.division}` : ''}
            action={<Chip tone="ghost">{filteredTransactions.length} ROWS</Chip>}
          >
            {/* Division tabs */}
            <div className="tabs">
              {wallets.map((w, i) => (
                <button
                  key={w.division}
                  className={`tab${i === currentTab ? ' active' : ''}`}
                  onClick={() => {
                    setCurrentTab(i);
                    setSelectedYear('all');
                    setTypeFilter('all');
                    setSearchFilter('');
                  }}
                >
                  <span>Division {w.division}</span>
                  <span className="sublabel">{fmtISKShort(w.balance)} ISK</span>
                </button>
              ))}
            </div>

            {/* Filter bar */}
            <div className="filter-bar">
              <label className="hud-input search">
                {I.Search}
                <input
                  placeholder="Search item, client, description…"
                  value={searchFilter}
                  onChange={e => setSearchFilter(e.target.value)}
                />
              </label>

              <select
                className="hud-select"
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="market">Market Transactions</option>
                <option value="journal">Journal Entries</option>
                <option value="buy">Market Buy Only</option>
                <option value="sell">Market Sell Only</option>
              </select>

              {availableYears.length > 0 && (
                <select
                  className="hud-select"
                  value={selectedYear}
                  onChange={e => setSelectedYear(e.target.value)}
                >
                  <option value="all">All Years</option>
                  {availableYears.map(y => (
                    <option key={y} value={y.toString()}>{y}</option>
                  ))}
                </select>
              )}
            </div>

            {/* AG Grid */}
            {currentWallet && (
              currentWallet.transactions.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)' }}>
                  No transactions found for Division {currentWallet.division}.
                </div>
              ) : (
                <div className="hud-grid">
                  <AgGridReact
                    theme={gridTheme}
                    rowData={filteredTransactions}
                    columnDefs={txColumnDefs}
                    defaultColDef={defaultColDef}
                    domLayout="autoHeight"
                    rowHeight={56}
                    headerHeight={40}
                    floatingFiltersHeight={36}
                    pagination
                    paginationPageSize={50}
                    paginationPageSizeSelector={[25, 50, 100]}
                    getRowId={(params) =>
                      params.data.transactionId?.toString() ||
                      `${params.data.date}-${params.data.amount}-${params.data.refType}`
                    }
                    animateRows
                  />
                </div>
              )
            )}
          </Panel>
        </>
      )}

      {/* Pending Fees Modal — kept as MUI Dialog (complex behavior) */}
      <Dialog
        open={pendingFeesModalOpen}
        onClose={() => setPendingFeesModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <RequestFeeIcon color="error" />
            Pending Fee Payments
          </span>
        </DialogTitle>
        <DialogContent>
          {pendingFeesByEvent.length === 0 ? (
            <Alert severity="success" sx={{ mt: 1 }}>All fee payments have been processed!</Alert>
          ) : (
            pendingFeesByEvent.map((event) => (
              <div key={event.eventId} style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontWeight: 600 }}>{event.eventName}</span>
                  <Chip tone="ghost">{new Date(event.eventDate).toLocaleDateString()}</Chip>
                  <Chip tone="amber">{event.feeType === 'per_account' ? 'Per Account' : 'Per Main'}</Chip>
                </div>
                <table className="hud-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Character</th>
                      <th className="num">Expected</th>
                      <th className="num">Received</th>
                      <th className="num">Open</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.attendees.map((attendee) => (
                      <tr key={attendee.characterId} className={attendee.isPaid ? 'active' : ''}>
                        <td>
                          <span className="cell-with-portrait">
                            <Avatar
                              src={`https://images.evetech.net/characters/${attendee.characterId}/portrait?size=32`}
                              sx={{ width: 28, height: 28 }}
                            />
                            <span>
                              <span className="cell-strong">{attendee.characterName}</span>
                              {attendee.feeCode && (
                                <span className="mono cell-mute" style={{ display: 'block', fontSize: 10 }}>
                                  {attendee.feeCode}
                                </span>
                              )}
                            </span>
                          </span>
                        </td>
                        <td className="num">{fmtISK(attendee.expectedFee)}</td>
                        <td className={`num ${attendee.receivedAmount > 0 ? 'cell-amt-pos' : 'cell-mute'}`}>
                          {fmtISK(attendee.receivedAmount)}
                        </td>
                        <td className={`num ${attendee.isPaid ? 'cell-amt-pos' : 'cell-amt-neg'}`}>
                          {attendee.isPaid ? 'Paid' : fmtISK(attendee.openAmount)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <ToggleButtonGroup
                            value={attendee.status}
                            exclusive
                            onChange={(e, newStatus) => {
                              if (newStatus !== null) {
                                handleUpdateAttendanceStatus(event.eventId, attendee.characterId, newStatus);
                              }
                            }}
                            size="small"
                          >
                            <ToggleButton value="attended" sx={{ '&.Mui-selected': { backgroundColor: 'success.main', color: 'white' } }}>
                              <Tooltip title="Attended"><AttendedIcon fontSize="small" /></Tooltip>
                            </ToggleButton>
                            <ToggleButton value="request_fee" sx={{ '&.Mui-selected': { backgroundColor: 'error.main', color: 'white' } }}>
                              <Tooltip title="Request Fee"><RequestFeeIcon fontSize="small" /></Tooltip>
                            </ToggleButton>
                            <ToggleButton value="fee_paid" sx={{ '&.Mui-selected': { backgroundColor: 'warning.main', color: 'white' } }}>
                              <Tooltip title="Fee Paid"><FeePaidIcon fontSize="small" /></Tooltip>
                            </ToggleButton>
                            <ToggleButton value="excused" sx={{ '&.Mui-selected': { backgroundColor: 'info.main', color: 'white' } }}>
                              <Tooltip title="Excused"><ExcusedIcon fontSize="small" /></Tooltip>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingFeesModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Wallet;
